import React, { useRef } from "react";
import { Mic, Send, Square, Paperclip, X, Image as ImageIcon, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function VoiceBar({ text, setText, onSend, onMic, listening, speaking, disabled,
                           micSupported, attachments = [], onAttach, onRemoveAttachment, uploading }) {
  const fileRef = useRef(null);
  const hasAtt = attachments.length > 0;

  const submit = (e) => {
    e.preventDefault();
    if ((text.trim() || hasAtt) && !disabled) onSend(text.trim());
  };

  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length && onAttach) onAttach(files);
    e.target.value = "";
  };

  return (
    <form onSubmit={submit} className="glass-panel rounded-2xl p-2" data-testid="voice-bar">
      {(hasAtt || uploading) && (
        <div className="flex flex-wrap gap-2 px-1 pt-1 pb-2" data-testid="attachment-preview">
          {attachments.map((a) => (
            <div key={a.fname} data-testid="attachment-chip" className="relative group rounded-lg overflow-hidden border border-white/10 bg-white/5">
              {a.kind === "image" ? (
                <img src={`${BACKEND}${a.url}`} alt={a.name} className="w-14 h-14 object-cover" />
              ) : (
                <div className="w-14 h-14 flex flex-col items-center justify-center text-slate-300 gap-1 px-1">
                  <FileText className="w-5 h-5" /><span className="text-[9px] truncate max-w-[52px]">{a.name}</span>
                </div>
              )}
              <button type="button" data-testid="attachment-remove" onClick={() => onRemoveAttachment(a.fname)}
                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 flex items-center justify-center text-white">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {uploading && (
            <div className="w-14 h-14 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center">
              <ImageIcon className="w-5 h-5 text-mono-300 animate-pulse" />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        {micSupported && (
          <button type="button" data-testid="mic-input-button" onClick={onMic}
            title={listening ? "Stop" : "Speak"}
            className={`relative w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-all ${listening ? "text-[#000000]" : "text-mono-300 hover:bg-white/5"}`}
            style={listening ? { background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)", boxShadow: "0 0 24px rgba(235,235,235,0.4)" } : { border: "1px solid rgba(235,235,235,0.2)" }}>
            {listening && <span className="ripple" style={{ width: 44, height: 44, borderColor: "rgba(235,235,235,0.5)" }} />}
            {speaking ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
          </button>
        )}

        <button type="button" data-testid="attach-button" onClick={() => fileRef.current?.click()}
          title="Attach photos or files"
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-slate-300 hover:text-mono-300 hover:bg-white/5 transition-all"
          style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
          <Paperclip className="w-5 h-5" />
        </button>
        <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.txt,.csv,.doc,.docx"
          onChange={pick} className="hidden" data-testid="attach-file-input" />

        <input data-testid="chat-text-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder={listening ? "Listening…" : hasAtt ? "Add an instruction (e.g. combine these photos)…" : "Say something, type it, or attach a photo…"}
          disabled={disabled}
          className="flex-1 bg-transparent px-2 py-2 text-[15px] outline-none placeholder:text-slate-500" />

        <button type="submit" data-testid="send-message-button" disabled={disabled || (!text.trim() && !hasAtt)}
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-[#000000] disabled:opacity-40 transition-all"
          style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </form>
  );
}
